/** Shared visual parts for the Queues module. */
import React from 'react';
import { Box, Chip, Avatar, Typography, Tooltip } from '@mui/material';
import {
  PublicRounded,
  TouchAppRounded,
  LockRounded,
  StarRounded,
} from '@mui/icons-material';
import { colors, brand } from '../../theme/theme';
import { Queue, QueueStatus, QueueBookingMode, STATUS_CHIP, capacityOf } from '../../api/queueTypes';
import { initials } from '../../auth/session';

export const QueueStatusChip: React.FC<{ status: QueueStatus }> = ({ status }) => {
  const s = STATUS_CHIP[status];
  const map = {
    success: { bg: '#ECFDF5', fg: colors.success },
    warning: { bg: '#FFFBEB', fg: brand.status.in_progress },
    default: { bg: colors.gray100, fg: colors.gray600 },
  }[s.cls];
  return <Chip label={s.label} size="small" sx={{ bgcolor: map.bg, color: map.fg, fontWeight: 600, height: 24 }} />;
};

const MODE_ICON: Record<QueueBookingMode, React.ReactElement> = {
  public: <PublicRounded sx={{ fontSize: 16 }} />,
  controlled_public: <TouchAppRounded sx={{ fontSize: 16 }} />,
  private: <LockRounded sx={{ fontSize: 16 }} />,
};
const MODE_LABEL: Record<QueueBookingMode, string> = {
  public: 'Public',
  controlled_public: 'Self-controlled',
  private: 'Private',
};

export const ModeBadge: React.FC<{ mode: QueueBookingMode; withLabel?: boolean }> = ({ mode, withLabel }) => (
  <Tooltip title={MODE_LABEL[mode]}>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colors.textSecondary }}>
      {MODE_ICON[mode]}
      {withLabel && <Typography sx={{ fontSize: 12.5 }}>{MODE_LABEL[mode]}</Typography>}
    </Box>
  </Tooltip>
);

export const Rating: React.FC<{ value: number }> = ({ value }) =>
  value > 0 ? (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: brand.status.in_progress }}>
      <StarRounded sx={{ fontSize: 15 }} />
      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{value.toFixed(1)}</Typography>
    </Box>
  ) : null;

export const CapacityBar: React.FC<{ queue: Queue }> = ({ queue }) => {
  const cap = capacityOf(queue);
  const color = cap.level === 'high' ? brand.capacity.high : cap.level === 'medium' ? brand.capacity.medium : brand.capacity.low;
  return (
    <Box>
      <Box sx={{ height: 8, borderRadius: 99, bgcolor: colors.gray100, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${cap.pct}%`, bgcolor: color, borderRadius: 99, transition: 'width .4s' }} />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
        <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>{cap.label}</Typography>
        <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
          {queue.tokens_booked_today}/{queue.max_tokens_today || 0} • {cap.pct}%
        </Typography>
      </Box>
    </Box>
  );
};

export const ProviderStack: React.FC<{ providers: Queue['providers'] }> = ({ providers }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ display: 'flex' }}>
      {providers.slice(0, 3).map((p, idx) => (
        <Avatar
          key={p.id}
          sx={{ width: 28, height: 28, fontSize: 11, ml: idx === 0 ? 0 : '-8px', border: `2px solid ${colors.backgroundPaper}`, background: brand.gradProvider }}
        >
          {p.initials || initials(p.display_name || 'P')}
        </Avatar>
      ))}
    </Box>
    <Typography noWrap sx={{ fontSize: 12.5, color: colors.textSecondary, maxWidth: 180 }}>
      {providers.map((p) => p.display_name).filter(Boolean).join(', ') || '—'}
    </Typography>
  </Box>
);
